/**
 * @file 金额输入区相关hook
 */
import { useState, useEffect } from 'react';
import { StakeOption } from '@/types/stake';
import BigNumber from 'bignumber.js';
import { useFm } from '@web3/base-hooks';
import { getTokenBalance } from '@/services/wallet';
import { useParams } from '@web3/base-hooks';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { CoinInfo } from '@/types/common';
import { getAddress, isNativeToken } from '@/utils/common';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { ChainCodeTypes } from '@/types/chain';
import { getTokenAddress } from '@/config/address';

/**
 * 获取用户余额，包括当前币种和gas费币种
 */
export const useBalance = (option: Partial<StakeOption>) => {
  const { currentStakeToken } = option as StakeOption;
  const { poolInfo } = usePoolInfoContext();
  const [balance, setBalance] = useState('');
  const [balanceIsLoading, setBalanceIsLoading] = useState(true);
  const [nativeBalance, setNativeBalance] = useState('');
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);

  const isSpecialChain = () => {
    const specialChain: string[] = [ChainCodeTypes.SUI, ChainCodeTypes.TON];
    return specialChain.includes(poolInfo.chainCode);
  };

  const fetchBalance = async () => {
    if (!walletAddress) return;

    let tokenAddress = getAddress(currentStakeToken.contractAddress);
    if (isSpecialChain()) {
      tokenAddress = currentStakeToken.contractAddress;
    }

    const { success, result } = await getTokenBalance({
      chainId: poolInfo.chainId,
      tokenAddress,
      walletAddress,
    });

    setBalanceIsLoading(false);

    if (success) {
      setBalance(result.balance);
    }
  };

  const fetchNativeBalance = async () => {
    if (!walletAddress) return;
    let nativeTokenAddress = '';

    if (isSpecialChain()) {
      nativeTokenAddress = getTokenAddress(poolInfo.chainCode);
    }

    const { success, result } = await getTokenBalance({
      chainId: poolInfo.chainId,
      tokenAddress: nativeTokenAddress,
      walletAddress,
    });

    if (success) {
      setNativeBalance(result.balance);
    }
  };

  useEffect(() => {
    if (currentStakeToken) {
      fetchBalance();
    }
  }, [currentStakeToken]);

  useEffect(() => {
    fetchNativeBalance();
  }, []);

  return {
    balance,
    balanceIsLoading,
    setBalance,
    nativeBalance,
  };
};

/**
 * 输入框相关状态
 */
export const useInputStatus = (option: Partial<StakeOption>) => {
  const {
    stakeAmount,
    balance,
    nativeBalance,
    gasStore,
    currentStakeToken,
    onChangeTextError,
  } = option as StakeOption;
  const { poolInfo } = usePoolInfoContext();
  const fm = useFm();
  const { min, max } = poolInfo?.poolExtendDTO?.stakeRange || {};
  const isShowMax = BigNumber(balance).gt(0);

  const getInputErrorText = () => {
    if (min && BigNumber(stakeAmount).lt(min)) {
      return fm('createMinInvestment', {
        amount: min,
      });
    }

    if (max && BigNumber(stakeAmount).gt(max)) {
      return fm('createMaxInvestment', {
        amount: max,
      });
    }

    // 用户输入要大于余额
    if (BigNumber(stakeAmount).gt(balance)) {
      return fm('token_balance_due', { token: currentStakeToken.symbol });
    }

    // 如果为主币，gas费是否足够
    if (isNativeToken(currentStakeToken.contractAddress)) {
      if (BigNumber(stakeAmount).plus(gasStore.amount).gt(balance)) {
        return fm('gas_fee_less');
      }
    }

    // 如果为非主币，gas费是否足够
    if (BigNumber(gasStore.amount).gt(nativeBalance)) {
      return fm('gas_fee_less');
    }
    return '';
  };

  const inputErrorText = getInputErrorText();
  onChangeTextError(inputErrorText);

  return {
    inputErrorText,
    isShowMax,
  };
};

/**
 * 选择币种弹窗相关状态
 */
export const useSelectCoinModal = () => {
  const [showSelectCoinModal, setShowSelectCoinModal] = useState(false);

  const onChangeShowSelectCoinModal = (v: boolean) => {
    setShowSelectCoinModal(v);
  };

  return {
    showSelectCoinModal,
    onChangeShowSelectCoinModal,
  };
};

/**
 * 存放swap相关状态
 */
export const useToSwap = (currentStakeToken: CoinInfo) => {
  const { poolInfo } = usePoolInfoContext();
  const { locale } = useParams();

  const toSwap = () => {
    if (window.top) {
      window.top.open(
        `/${locale}/web3/swap-bridge#toChain=${poolInfo.chainId}&toToken=${currentStakeToken.contractAddress}`
      );
    }
  };

  return {
    toSwap,
  };
};

export const useDeposite = () => {
  const [showDeposit, setShowDeposit] = useState(false);

  const onChangeShowDeposite = (v: boolean) => {
    setShowDeposit(v);
  };

  return {
    showDeposit,
    onChangeShowDeposite,
  };
};
